import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
	Box,
	Chip,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { Exposure } from '../../../types/policy';
import { FastForm } from '../../fastForm';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import { getPerilName } from '../util_sharedEndorsementAndRenewal';
import AddPerilModal from './AddRenewalPerilModal';
import { isRenewalPerilActive } from './util';

interface Props {
	authData: AuthData;
	userHasWriteAccess?: boolean;
	renewalData: RenewalData;
	errorData: ErrorData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
		marginRight: 'auto',
	},
	title: {
		textTransform: 'capitalize',
		flexGrow: 1,
		fontSize: '18px',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

/**
 * Peril detail page for existing perils on existing exposures on renewals,
 * The perilId in the url will be the peril's displayId.
 */
const ExistingPerilDetails = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const navigate = useNavigate();
	const params = useParams();
	const { pathname } = useLocation();

	const { userHasWriteAccess, renewalData, authData } = props;

	const {
		renewalUpdateObj,
		productConfig,
		setLoading,
		fields: form,
		saveFastForm,
	} = renewalData;
	const selectedExposure = renewalData.selectedExposure as Exposure;

	const { state } = renewalUpdateObj;
	const { perilId } = params;

	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);

	const selectedPeril = selectedExposure.perils.find(
		(peril) => peril.displayId === perilId,
	);

	if (!selectedPeril || !selectedPeril.characteristics) {
		return null;
	}

	const policyConfig = productConfig.policyConfiguration;
	const exposureConfig = policyConfig.exposures.find(
		(exposure) => exposure.name === selectedExposure.name,
	);
	const perilConfig = exposureConfig.perils.find(
		(p) => p.name === selectedPeril.name,
	);
	const perilConfigs = exposureConfig.perils;

	const displayName = getPerilName(
		selectedPeril,
		perilConfig.displayName,
		selectedExposure,
		renewalUpdateObj,
		null,
	);
	const isInEditMode =
		authData.hasPermission('renewal.update') && state === 'application';

	const isActive = isRenewalPerilActive(
		selectedPeril,
		renewalData.policy,
		renewalUpdateObj,
	);

	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const removePeril = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			const perilLocator = selectedPeril.locator;
			const exposureLocator = selectedExposure.locator;
			await saveFastForm(getValues(), dirtyFields, reset, {
				removePeril: { perilLocator, exposureLocator },
			});
			successToast('Successfully removed peril');
			navigate(
				`/policy/${params.policyId}/renewals/${params.renewalId}/exposures/${params.exposureId}/perils`,
			);
		} catch (error) {
			errorToast('Error removing peril');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const renderRemovePeril = () => {
		return (
			<Tooltip title={'Remove ' + t('peril.singular.upper')}>
				<IconButton
					aria-label='Remove'
					sx={{ position: 'relative', top: '8px' }}
					onClick={() => setIsConfirmModalOpen(true)}
					className={classes.removeIcon}
				>
					<DeleteIcon
						sx={{
							'&:hover': {
								backgroundColor: '#f4433',
							},
						}}
					/>
				</IconButton>
			</Tooltip>
		);
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={selectedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.locator}
			/>
		);
	};

	if (!renewalUpdateObj) {
		return null;
	}

	return (
		<React.Fragment key={pathname}>
			<Box className={classes.titleWrapper}>
				<Typography className={classes.title}>{displayName}</Typography>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={Link}
						to={`auxData`}
					>
						Aux Data
					</MaterialLink>
				)}
				{!isActive && (
					<Chip
						color='default'
						label='Inactive'
						icon={<ErrorOutlineRoundedIcon />}
					/>
				)}
				{isInEditMode && renderRemovePeril()}
				{isConfirmModalOpen && renderRemovePerilModal()}
			</Box>
			<hr />
			{!hidePerilLimitsEnabled ? <h4>Limits</h4> : null}
			<Grid container spacing={2}>
				<FastForm
					formName={selectedExposure.locator + '.' + selectedPeril.locator}
					form={form}
					configVersion={productConfig.configVersion}
					authData={props.authData}
					productName={productConfig.name}
					disabled={!isInEditMode || userHasWriteAccess || !isActive}
					save={saveFastForm}
					setLoading={setLoading}
					disableExternalCalls={true}
					effectiveDate={renewalUpdateObj.overrideResourcesAsOfTimestamp}
				/>
			</Grid>
			{isInEditMode && renderActions()}
		</React.Fragment>
	);
};

export default withRenewalContext<Props>(ExistingPerilDetails);
